import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { compareAsc, parseISO } from "date-fns";

import { Alert } from "../../components/Alert";

import { getNextEvent, isRegistrationOpen, parseEventDate } from "../../utils/eventHelpers";
import { formatDateBR } from "../../utils/formatDateBR";

import { useStore } from "../../contexts";
import { Gallery, GalleryImage, useImages } from "../../contexts";
import { useMessages } from "../../contexts";
import { useEvents } from "../../contexts/Eventos";

import { BanknotesIcon, CalendarDaysIcon, MapPinIcon } from "@heroicons/react/24/outline";
import { formatCurrency } from "../../utils/formatCurrency";

const buttonClass = "inline-flex items-center justify-center rounded-md bg-brand-500 px-6 py-3 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-brand-600";

export default function Home() {
  const sectionRef = useRef<HTMLDivElement>(null);

  const { getStore, store } = useStore();
  const { getMidias, gallery } = useImages();
  const { events, players } = useEvents();

  const [alert, setAlert] = useState("");
  const [alertMessage, setAlertMessage] = useState("");

  const { sendMessage, data, setData } = useMessages();
  const navigate = useNavigate();

  useEffect(() => {
    getMidias();
    getStore();
  }, [getMidias, getStore]);

  const activeEvent = useMemo(() => {
    return getNextEvent(events);
  }, [events]);

  const daysLeft = useMemo(() => {
    if (!activeEvent) {
      return 0;
    }

    const now = new Date();

    return Math.ceil(
      (parseEventDate(activeEvent.date).getTime() - now.getTime()) /
      (1000 * 60 * 60 * 24)
    );
  }, [activeEvent]);

  const remainingSlots = useMemo(() => {
    if (!activeEvent) {
      return 0;
    }

    return activeEvent.max_players -
      players.filter((p) => p.event_id === activeEvent.id).length;
  }, [activeEvent, players]);

  const registrationOpen = useMemo(() => {
    return activeEvent ? isRegistrationOpen(activeEvent) : false;
  }, [activeEvent]);

  const handleData = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setData(prev => ({
      ...prev,
      [name]: value
    }));
  }, [setData]);

  const dataValidation = useCallback(() => {
    const values = Object.values(data);
    return !values.some((value) => value === undefined || value === null || value === "");
  }, [data]);

  const sendData = useCallback(async () => {
    const success = await sendMessage();

    if (success) {
      setAlert("success");
      setAlertMessage("Seu formulário foi enviado com sucesso!");
    } else {
      setAlert("error");
      setAlertMessage("Erro ao enviar seu formulário!");
    }

    setTimeout(() => {
      setAlert("");
    }, 2000);
  }, [sendMessage]);

  const scrollToSection = () => {
    sectionRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  function formatPhoneNumber(phoneNumber: string): string {
    const cleanedNumber = phoneNumber.replace(/\D/g, "");
    return cleanedNumber.replace(/(\d{3})(\d{5})(\d{4})/, "($1) $2-$3");
  }

  const handleContactChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const formattedNumber = formatPhoneNumber(event.target.value);

    setData(prev => ({
      ...prev,
      telefone: formattedNumber
    }));
  }, [setData]);

  const galleryOrdenada = useMemo(() => {
    return [...(gallery || [])].sort((a: Gallery, b: Gallery) => {
      const dataA = parseISO(a.data);
      const dataB = parseISO(b.data);
      return compareAsc(dataB, dataA);
    });
  }, [gallery]);

  return (
    <div className="w-full bg-[#f4f4f4] text-gray-800">
      <section
        className="relative flex h-[50vh] min-h-[460px] items-center justify-center bg-cover bg-center px-4"
        style={{ backgroundImage: "url('/background.png')" }}
      >
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative z-10 flex flex-col items-center gap-3 text-center text-white">
          <img src="/logo.png" alt="Logo elite" className="bg-black rounded-full mx-auto h-44 w-44 object-contain" />
          <h1 className="text-5xl font-bold tracking-wider md:text-7xl">A.S.A</h1>
          <h2 className="text-xl uppercase tracking-[0.35em] text-brand-400 md:text-2xl">Força e Honra</h2>
          <button onClick={scrollToSection} className={buttonClass}>Saiba Mais</button>
        </div>
      </section>

      <section ref={sectionRef} className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 py-12 md:px-8">
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold uppercase text-brand-700 pb-4">Sobre as equipes</h2>
          <h3 className="text-lg font-medium text-gray-500">Elite Team Airsoft</h3>
        </div>

        <div className="grid items-center gap-8 rounded-xl bg-white p-6 shadow md:grid-cols-[220px_1fr]">
          <img src="/logoelite.png" alt="Logo elite" className="mx-auto h-44 w-44 object-contain" />
          <div className="space-y-4 text-gray-700">
            <p className="flex items-start gap-3">
              Associação Santiaguense de Airsoft engloba o Elite Team Airsoft, equipe fundada em dezembro de 2019 por Cedenir Colpo na cidade de Santiago-RS, para prática de Airsoft.
            </p>
            <p>
              Utilizando como base simulações de ações militares (Milsim), o Elite Team Airsoft conta hoje com cerca de 20 jogadores. Seguindo os passos dos pioneiros do esporte levando sempre para campo os valores de honra, respeito, disciplina e amizade.
            </p>
          </div>
        </div>
        <span className="text-center text-sm italic text-gray-600">Fortitudine et disciplina, Elite Team virtutis exemplar est.</span>
        <h3 className="text-lg font-medium text-gray-500 text-center md:text-left">Alcatéia Airsoft Team</h3>
        <div className="grid items-center gap-8 rounded-xl bg-white p-6 shadow md:grid-cols-[220px_1fr]">
          <img src="/logoalcateia.png" alt="Logo alcatéia" className="mx-auto h-44 w-44 object-contain" />
          <div className="space-y-4 text-gray-700">
            <p className="flex items-start gap-3">
              Associação Santiaguense de Airsoft engloba também o Alcatéia Airsoft Team.
            </p>
            <p>
              Equipe criada no inicio de 2018 com os pilares de honra, lealdade e companheirismo.
            </p>
          </div>
        </div>        
      </section>

      {activeEvent && (
        <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-8">

          <div className="mb-6">
            <h2 className="text-2xl font-bold text-brand-700">
              Próximo evento
            </h2>
          </div>

          <div
            onClick={() => navigate(`/evento/${activeEvent.slug}`)}
            className="group cursor-pointer overflow-hidden rounded-2xl bg-white shadow-md transition-all duration-300 hover:shadow-xl border border-gray-100"
          >
            {/* 1. IMAGEM DO EVENTO NO TOPO */}
            <div className="relative w-full aspect-video max-h-[560px] overflow-hidden">
              <img
                src={activeEvent.cover_image}
                alt={activeEvent.name}
                className="w-full h-full object-fill block transition-transform duration-300 group-hover:scale-[1.01]"
              />

              {/* Badge de status */}
              <div className="absolute top-4 left-4 sm:top-5 sm:left-5 z-10">
                <span className={`rounded-full px-4 py-1.5 text-xs font-bold text-white shadow-lg uppercase tracking-wider ${registrationOpen ? "bg-red-600" : "bg-amber-500"}`}>
                  {!registrationOpen
                    ? "Inscrições em breve"
                    : remainingSlots === 0
                      ? "Lotado"
                      : "Inscrições abertas"}
                </span>
              </div>
            </div>

            {/* 2. INFORMAÇÕES BEM DISPOSTAS ABAIXO DA IMAGEM */}
            <div className="p-6 sm:p-8 flex flex-col gap-6">

              {/* Título e Contadores */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-6">
                <div>
                  <span className="text-xs font-bold uppercase tracking-widest text-brand-600">
                    Operação Oficial A.S.A
                  </span>
                  <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mt-1">
                    {activeEvent.name}
                  </h3>
                </div>

                <div className="flex items-center gap-3">
                  <div className="rounded-xl bg-gray-50 border border-gray-200/80 px-4 py-2.5 text-center min-w-[110px]">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Dias restantes</p>
                    <p className="text-2xl font-black text-brand-700 leading-tight mt-0.5">
                      {daysLeft}
                    </p>
                  </div>

                  {registrationOpen ? (
                    <div className="rounded-xl bg-brand-50 border border-brand-200/80 px-4 py-2.5 text-center min-w-[110px]">
                      <p className="text-[10px] uppercase tracking-wider text-brand-700 font-bold">Vagas restantes</p>
                      <p className="text-2xl font-black text-brand-700 leading-tight mt-0.5">
                        {remainingSlots}
                      </p>
                    </div>
                  ) : (
                    <div className="rounded-xl bg-amber-50 border border-amber-200/80 px-4 py-2.5 text-center min-w-[110px]">
                      <p className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">Inscrições</p>
                      <p className="text-sm font-black text-amber-600 leading-tight mt-1.5 uppercase">
                        Em breve
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Informações de Local/Data/Preço + Mapa */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div className="space-y-4 text-sm sm:text-base text-gray-700">
                  <div className="flex items-center gap-3">
                    <CalendarDaysIcon className="h-5 w-5 text-blue-500 shrink-0" />
                    <span className="font-semibold text-gray-900">{formatDateBR(activeEvent.date)}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <MapPinIcon className="h-5 w-5 text-red-500 shrink-0" />
                    <span className="font-medium text-gray-800">{activeEvent.location}</span>
                  </div>

                  <div className="flex items-center gap-3">
                    <BanknotesIcon className="h-5 w-5 text-green-600 shrink-0" />
                    <span className="font-semibold text-gray-900">
                      {activeEvent.price ? formatCurrency(activeEvent.price) : "Gratuito"}
                    </span>
                  </div>

                  <div className="pt-2">
                    <button className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl bg-brand-500 px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white shadow-sm transition hover:bg-brand-600">
                      Ver detalhes e local &rarr;
                    </button>
                  </div>
                </div>

                {/* Mapa */}
                <div className="overflow-hidden rounded-xl border border-gray-200 h-[170px] sm:h-[190px] w-full">
                  <iframe
                    title="Mapa do evento"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(activeEvent.location)}&z=14&output=embed`}
                    className="h-full w-full border-0"
                    loading="lazy"
                  />
                </div>
              </div>

            </div>

          </div>

        </section>
      )}

      {((galleryOrdenada?.length || 0) > 0 || (store?.length || 0) > 0) && (
        <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-brand-700">
              Atividade recente no portal
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

            {/* GALERIA */}
            {(galleryOrdenada?.length || 0) > 0 && (
              <div
                className="group cursor-pointer overflow-hidden rounded-xl bg-white shadow transition hover:shadow-lg"
                onClick={() => navigate("/galery")}
              >
                <h2 className="p-5 text-lg font-semibold text-gray-700">
                  Última galeria criada
                </h2>

                {galleryOrdenada && galleryOrdenada[0] && (
                  <div className="relative">

                    <img
                      src={
                        galleryOrdenada[0]?.images
                          ?.slice()
                          .sort((a: GalleryImage, b: GalleryImage) => a.order - b.order)[0]?.url
                      }
                      alt="Imagem da galeria"
                      className="h-[240px] w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80" />

                    <div className="absolute bottom-0 left-0 flex w-full flex-col gap-1 p-4 text-white">

                      <span className="text-xs uppercase tracking-wider text-brand-300">
                        Galeria
                      </span>

                      <h3 className="text-lg font-semibold">
                        {galleryOrdenada[0].description}
                      </h3>

                      <span className="text-sm opacity-80">
                        {formatDateBR(galleryOrdenada[0].data)}
                      </span>

                      <span className="mt-1 text-xs opacity-70">
                        {galleryOrdenada[0].images?.length} fotos
                      </span>

                    </div>

                  </div>
                )}
              </div>
            )}

            {/* LOJA */}
            {(store?.length || 0) > 0 && (
              <div
                className="group cursor-pointer overflow-hidden rounded-xl bg-white shadow transition hover:shadow-lg"
                onClick={() => navigate("/loja")}
              >
                <h2 className="p-5 text-lg font-semibold text-gray-700">
                  Último item anunciado
                </h2>

                {store && store[0] && (
                  <div className="relative">

                    <img
                      src={store[0].url}
                      alt="Imagem da loja"
                      className="h-[240px] w-full object-cover transition duration-500 group-hover:scale-105"
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80" />

                    <div className="absolute bottom-0 left-0 flex w-full flex-col gap-1 p-4 text-white">

                      <span className="text-xs uppercase tracking-wider text-brand-300">
                        Loja
                      </span>

                      <h3 className="text-lg font-semibold">
                        {store[0].text}
                      </h3>

                      <span className="text-sm">
                        {formatCurrency(Number(store[0].value))}
                      </span>

                    </div>

                  </div>
                )}
              </div>
            )}

          </div>
        </section>
      )}

      <section className="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 pb-12 md:px-8">

        <div className="rounded-xl bg-brand-900 p-6 text-white shadow">
          <h2 className="text-2xl font-bold uppercase">Código de honra</h2>
          <span className="text-sm text-brand-400">
            Associação Santiaguense de Airsoft
          </span>
        </div>

        {/* Conduta */}
        <div className="rounded-xl bg-white p-5 shadow md:grid md:grid-cols-[1fr_180px] md:items-center md:gap-6">

          <div>
            <div className="mb-3 flex items-start gap-3 md:block">
              <img
                src="/safe.png"
                alt="Safe"
                className="h-10 w-10 rounded-md object-cover md:hidden"
              />

              <div>
                <h2 className="text-lg font-bold text-brand-700 md:text-2xl">
                  Conduta em campo
                </h2>
                <p className="text-sm text-gray-600">
                  Minimizando risco de acidentes
                </p>
              </div>
            </div>

            <ul className="list-disc space-y-2 pl-5 text-gray-700">
              <li>Óculos de proteção é equipamento obrigatório.</li>
              <li>Arma sempre travada e desmuniciada na safe zone.</li>
              <li>Manter o FPS da sua arma correspondente ao limite estipulado.</li>
              <li>Considerar uso de equipamentos diversos, capacete, coturno e joelheira.</li>
            </ul>
          </div>

          <img
            src="/safe.png"
            alt="Safe"
            className="mx-auto hidden h-40 w-40 rounded-lg object-cover md:block md:h-44 md:w-44"
          />
        </div>

        {/* Respeito */}
        <div className="rounded-xl bg-white p-5 shadow md:grid md:grid-cols-[180px_1fr] md:items-center md:gap-6">

          <img
            src="/respect.png"
            alt="Respeito"
            className="mx-auto hidden h-40 w-40 rounded-lg object-cover md:block md:h-44 md:w-44"
          />

          <div>
            <div className="mb-3 flex items-start gap-3 md:block">
              <img
                src="/respect.png"
                alt="Respeito"
                className="h-10 w-10 rounded-md object-cover md:hidden"
              />

              <h2 className="text-lg font-bold text-brand-700 md:text-2xl">
                Respeito e amizade
              </h2>
            </div>

            <p className="text-gray-700">
              O Airsoft é um esporte coletivo. O respeito e a seriedade são
              fundamentais para todos se divertirem e conviverem em harmonia.
            </p>
          </div>
        </div>

        {/* Honra */}
        <div className="rounded-xl bg-white p-5 shadow md:grid md:grid-cols-[1fr_180px] md:items-center md:gap-6">

          <div>
            <div className="mb-3 flex items-start gap-3 md:block">
              <img
                src="/honra.png"
                alt="Honra"
                className="h-10 w-10 rounded-md object-cover md:hidden"
              />

              <div>
                <h2 className="text-lg font-bold uppercase text-brand-700 md:text-2xl">
                  Jogue com honra
                </h2>
                <p className="text-sm text-gray-600">
                  O Airsoft necessita de um sistema de honra
                </p>
              </div>
            </div>

            <ul className="list-disc space-y-2 pl-5 text-gray-700">
              <li>Se houver incerteza sobre ter sido atingido, saia do jogo.</li>
              <li>Se o oponente não se acusou, ele não está eliminado.</li>
              <li>Comportamentos desonestos devem ser informados ao comando.</li>
            </ul>
          </div>

          <img
            src="/honra.png"
            alt="Honra"
            className="mx-auto hidden h-40 w-40 rounded-lg object-cover md:block md:h-44 md:w-44"
          />
        </div>

        {/* Leis */}
        <div className="rounded-xl bg-white p-5 shadow md:grid md:grid-cols-[180px_1fr] md:items-center md:gap-6">

          <img
            src="/laws.png"
            alt="Leis"
            className="mx-auto hidden h-40 w-40 rounded-lg object-cover md:block md:h-44 md:w-44"
          />

          <div>
            <div className="mb-3 flex items-start gap-3 md:block">
              <img
                src="/laws.png"
                alt="Leis"
                className="h-10 w-10 rounded-md object-cover md:hidden"
              />

              <div>
                <h2 className="text-lg font-bold text-brand-700 md:text-2xl">
                  Respeito às leis
                </h2>
                <p className="text-sm text-gray-600">
                  Controle feito pelo Exército Brasileiro
                </p>
              </div>
            </div>

            <ul className="list-disc space-y-2 pl-5 text-gray-700">
              <li>Transporte em case de proteção e com nota fiscal.</li>
              <li>A ponta laranja é obrigatória.</li>
            </ul>
          </div>
        </div>

        {/* Documentos */}
        <div className="rounded-xl bg-white p-6 shadow">
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-brand-700">
              Documentos do Recruta
            </h2>
            <span className="text-sm text-gray-500">
              Material obrigatório para novos integrantes
            </span>
          </div>

          <div className="flex flex-wrap gap-3">
            <a
              href="https://drive.google.com/file/d/1_KaephUv8DjEN-nuu_ojyre2rv2byw9V/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClass}
            >
              Manual do Recruta
            </a>

            <a
              href="https://drive.google.com/file/d/1a3H9dYVjDrudRe-Z9d6GX044vwxzS0fn/view?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className={buttonClass}
            >
              Regulamento Interno
            </a>
          </div>
        </div>

      </section>

      <section className="mx-auto flex w-full max-w-7xl flex-col gap-8 px-4 pb-12 md:px-8">
        <div className="mb-3 flex items-start gap-3 md:block">
          <img
            src="/balanca.png"
            alt="Legislação"
            className="h-10 w-10 rounded-md object-cover md:hidden"
          />

          <div>
            <h2 className="text-lg font-bold uppercase text-brand-700 md:text-2xl">
              Legislação no airsoft
            </h2>
            <p className="text-sm text-gray-600">
              Exército Brasileiro
            </p>
          </div>
        </div>

        <div className="grid items-center gap-8 rounded-xl bg-white p-6 shadow md:grid-cols-[220px_1fr]">
          <img
            src="/balanca.png"
            alt="Legislação"
            className="mx-auto hidden h-40 w-40 rounded-lg object-cover md:block md:h-44 md:w-44"
          />
          <div className="space-y-4 text-gray-700">
            <p>As regras para aquisição e importação de armas de pressão para airsoft no Brasil estão definidas na Portaria Nº 02 COLOG de 26 de fevereiro de 2010.</p>
            <p>Artigo 26 da Lei 10.826/03: são vedadas fabricação, venda, comercialização e importação de brinquedos, réplicas e simulacros que possam confundir com arma real.</p>
            <p>Para mais informações, acesse o site do EB: <a href="https://www.eb.mil.br" rel="noopener noreferrer" target="_blank" className="text-brand-600 hover:underline">www.eb.mil.br</a></p>
          </div>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-4 pb-12 md:px-8">
        <div className="rounded-xl bg-white p-6 shadow">
          {alert === "success" ? <Alert variant="success" type="inline">{alertMessage}</Alert> : alert === "error" && <Alert variant="error" type="inline">{alertMessage}</Alert>}

          <div className="mb-6 mt-2">
            <h2 className="text-2xl font-bold text-brand-700">Contato para jogos</h2>
            <h3 className="mt-2 text-sm text-gray-600">Se interessou pela equipe e gostaria de participar de um jogo aberto a convidados?</h3>
            <h3 className="text-sm text-gray-600">Entre em contato conosco e aliste-se!</h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="flex flex-col gap-3">
              <input type="text" name="nome" value={data.nome} placeholder="Digite seu nome*" onChange={handleData} className="rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-brand-500" />
              <input type="email" name="email" value={data.email} placeholder="Digite seu e-mail*" onChange={handleData} className="rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-brand-500" />
              <input type="tel" required value={data.telefone} placeholder="(055) 99999-9999" onChange={handleContactChange} className="rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-brand-500" />
              <input type="text" name="tempoPratica" value={data.tempoPratica} placeholder="A quanto tempo pratica o esporte*" onChange={handleData} className="rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-brand-500" />
            </div>
            <textarea name="mensagem" value={data.mensagem} placeholder="Deixe alguma mensagem*" onChange={(e) =>
              setData(prev => ({
                ...prev,
                mensagem: e.target.value
              }))
            } className="min-h-[190px] rounded-md border border-gray-300 px-4 py-3 outline-none focus:border-brand-500" />
          </div>

          <button type="button" disabled={!dataValidation()} className={`${buttonClass} mt-5 disabled:cursor-not-allowed disabled:opacity-60`} onClick={sendData}>Enviar</button>
        </div>
      </section>
    </div>
  );
}
